import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from "@angular/core";
import { getApp } from "../../app";
import { Thread } from "../../../../../shared/models/thread";

export type DashboardRecordType = "thread" | "manufacturer" | "part" | "task";

@Component({
    selector: "app-dashboard-record-table",
    templateUrl: "./dashboard-record-table.component.html",
    styleUrls: ["./dashboard-record-table.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DashboardRecordTableComponent {
  @Input() rows: Array<Record<string, unknown>> = [];
  @Input() columns: string[] = [];
  @Input() dataType: DashboardRecordType = "thread";
  @Input() pageSize = 25;
  @Input() total = 0;
  @Output() pageChange = new EventEmitter<number>();

  currentPage = 1;

  app = getApp((app) => {
    this.app = app;
  });

  get pagedRows() {
    return this.rows;
  }

  get totalPages() {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  get pageStart() {
    return this.total ? (this.currentPage - 1) * this.pageSize + 1 : 0;
  }

  get pageEnd() {
    return Math.min(this.currentPage * this.pageSize, this.total);
  }

  get visiblePages() {
    const start = Math.max(
      1,
      Math.min(this.currentPage - 2, this.totalPages - 4)
    );
    const end = Math.min(this.totalPages, start + 4);
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }

  setPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.pageChange.emit(page);
    }
  }

  columnKey(column: string) {
    return column.includes(".") ? column.split(".").slice(1).join(".") : column;
  }

  columnType(column: string) {
    return this.app.table.getColumnTypeDisplay(this.columnKey(column));
  }

  columnClass(column: string) {
    const key = this.columnKey(column).toLowerCase();
    return {
      "status-column": this.dataType === "thread" && key === "omfstatus",
    };
  }

  cellValue(row: Record<string, unknown>, column: string) {
    return row[this.columnKey(column)];
  }

  dueDateValue(row: Record<string, unknown>, column: string) {
    return Number(this.cellValue(row, column) ?? 0);
  }

  dueDateStyle(row: Record<string, unknown>) {
    return this.app.thread.getDueDateStyle(row as Thread);
  }

  navigateManufacturerCell(
    row: Record<string, unknown>,
    column: string
  ) {
    const key = this.columnKey(column);
    if (key === "partNumber") {
      this.app.part.navigateToPartDetails(String(row[key] ?? ""));
    } else if (
      (key === "manufacturerPartNumber" ||
        key === "manufacturerPartNumberRaw") &&
      row["_id"]
    ) {
      this.app.manufacturer.navigateToManufacturerDetails(String(row["_id"]));
    }
  }

  manufacturerOptionText(row: Record<string, unknown>, column: string) {
    const key = this.columnKey(column);
    return this.app.field.getFieldValueAsText(
      `manufacturer.${key}`,
      row[key]
    );
  }

  threadOptionText(row: Record<string, unknown>, column: string) {
    const key = this.columnKey(column);
    const fieldKey = this.dataType === "task" ? `post.${key}` : `thread.${key}`;
    return this.app.field.getOptionText(fieldKey, String(row[key] ?? ""));
  }

  isThreadStatusColumn(column: string) {
    return this.columnKey(column).toLowerCase().includes("status");
  }

  dashboardStatusColor(
    row: Record<string, unknown>,
    column: string
  ) {
    const key = this.columnKey(column);
    const style = this.app.field.getColor(
      `${this.dataType}.${key}`,
      String(row[key] ?? "")
    );
    if (String(row[key] ?? "").toLowerCase() === "unconfirmed") {
      return style;
    }
    if (!style.backgroundColor) {
      return {
        ...style,
        color: "inherit",
        borderColor: "transparent",
      };
    }

    return {
      ...style,
      color: "#fff",
      borderColor: "transparent",
    };
  }

  dashboardStatusText(
    row: Record<string, unknown>,
    column: string
  ) {
    const key = this.columnKey(column);
    return this.app.field.getOptionText(
      `${this.dataType}.${key}`,
      String(row[key] ?? "")
    );
  }

  rmHeaderLabel(column: string) {
    return this.dataType === "manufacturer"
      ? this.manufacturerHeaderLabel(column)
      : this.partHeaderLabel(column);
  }

  manufacturerHeaderLabel(column: string) {
    const key = this.columnKey(column);
    if (this.app.table.getColumnTypeDisplay(key) === "link") {
      return this.app.text.manufacturer.link;
    }
    return this.app.field.getTableLabel(`manufacturer.${key}`);
  }

  partHeaderLabel(column: string) {
    return this.app.partsTable.getPartsHeaderLabels(this.columnKey(column));
  }

  navigateToPart(row: Record<string, unknown>, column: string) {
    const value = row[this.columnKey(column)];
    if (value != null) {
      this.app.part.navigateToPartDetails(String(value));
    }
  }

  columnLabel(column: string) {
    try {
      return this.dataType === "thread"
        ? this.app.field.handleSpecialLogicLabels(column)
        : this.app.field.getTableLabel(
            column.includes(".")
              ? column
              : `${this.dataType}.${this.columnKey(column)}`
          );
    } catch {
      return this.columnKey(column);
    }
  }

  displayValue(row: Record<string, unknown>, column: string) {
    try {
      return this.getDisplayValue(row, column);
    } catch {
      const value = row[this.columnKey(column)];
      return value == null
        ? "—"
        : Array.isArray(value)
          ? value.join(", ")
          : String(value);
    }
  }

  private getDisplayValue(row: Record<string, unknown>, column: string) {
    const key = this.columnKey(column);
    const value = row[key];
    const displayType = this.app.table.getColumnTypeDisplay(key);

    if (value == null || value === "") return "—";
    if (displayType === "date" || displayType === "dueDate") {
      return this.formatDate(value, false);
    }
    if (displayType === "time") return this.formatDate(value, true);
    if (displayType === "optionText") {
      return this.app.field.getOptionText(`${this.dataType}.${key}`, String(value));
    }
    if (displayType === "getFieldValueAsText") {
      return this.app.field.getFieldValueAsText(`${this.dataType}.${key}`, value);
    }
    if (displayType === "getUser") {
      return this.app.users.getOptionName(String(value));
    }
    if (displayType === "limit") {
      return this.app.field.getItemsAsString(
        Array.isArray(value) ? value : [value],
        key
      );
    }
    return Array.isArray(value) ? value.join(", ") : String(value);
  }

  rowUrl(row: Record<string, unknown>) {
    if (!row["_id"]) return null;
    if (this.dataType === "thread") {
      return this.app.thread.getRouterURL(String(row["_id"]));
    }
    return null;
  }

  private formatDate(value: unknown, includeTime: boolean) {
    const date = new Date(Number(value));
    if (Number.isNaN(date.getTime())) return String(value);
    const dateValue = date.toISOString();
    return includeTime
      ? dateValue.slice(0, 19).replace("T", " ")
      : dateValue.slice(0, 10);
  }
}
